<template>
    <section class="comments">
        <div id="feed" ref="feed" v-keep-scroll-position class="comments__feed" :style="feedStyles">
            <infinite-loading direction="top" v-show="comments.canLoadMore()" @infinite="fetchComments" />

            <template class="lc-comment-group" v-for="(chunks, timestamp) in presentedComments">

                <aside class="date-divider" :key="timestamp">
                    <span class="date-divider__date">{{ timestamp | formattedDate }}</span>
                    <span class="date-divider__line" />
                </aside>

                <template v-for="chunk in chunks">
                    <article v-if="chunk.message" :key="chunk.id" class="lc-author-comment-block comment" :class="[`comment--${chunk.author.role}`, {'comment--alternate' : isCurrentUser(chunk.author.id) }]">
                        <transition-group name="comments" class="comment__group" tag="div">
                            <comment @reply="reply(comment)" @loaded="loaded" v-for="comment in chunk.comments" :comment="comment" :key="comment.id" />
                        </transition-group>
                        <avatar :user="chunk.author" />
                    </article>
                    <div v-else :key="chunk.id" class="activity">
                        <activity v-for="activity in chunk.comments" :key="activity.id" :activity="activity" class="lc-comment-item lc-activity-item activity__item" />
                    </div>
                </template>

            </template>
        </div>

        <footer ref="footer" @dragenter="$refs.commentsBox.showDropzone()" class="comments__footer">
            <p v-if="typingText" class="comments__typing">{{ typingText }}</p>
            <comment-box @input="updateFooterOffsetHeight" ref="commentsBox" @new-comment="scrollToBottom" />
        </footer>
    </section>
</template>

<script>
/* global Echo, app */
import { mapState, mapGetters } from 'vuex'
import CommentBox from './CommentBox.vue'
import Comment from './Comment'
import InfiniteLoading from 'vue-infinite-loading'
import Activity from './../Activity'
import Avatar from '../../Global/Avatar'
import Device from '../../../mixins/deviceAware'

export default {
    mixins: [Device],

    data () {
        return {
            typing: [],
            assetsLoaded: 0,
            footerOffsetHeight: 0
        }
    },

    watch: {
        footerOffsetHeight () {
            this.scrollToBottom()
        }
    },

    created () {
        this.$store.dispatch('job/fetchComments').then(() => {
            this.scrollToBottom()
        })

        Echo.private(`${app.currentTenant.id}_job_${this.job.id}`)
            .listenForWhisper('startTyping', (e) => {
                if (this.typing.indexOf(e.user.display_name) === -1) {
                    this.typing.push(e.user.display_name)
                }
            })
            .listenForWhisper('stopTyping', (e) => {
                if (this.typing.indexOf(e.user.display_name) !== -1) {
                    this.typing.splice(this.typing.indexOf(e.user.display_name), 1)
                }
            })
            .listen('CommentWasCreated', (e) => {
                this.$store.commit('job/ADD_COMMENT', (e.comment))
                this.scrollToBottom()
            })
            .listen('ReactedToComment', (e) => {
                this.$store.commit('job/ADD_REACTION', (e.reaction))
            })
            .listen('ReactionRemoved', (e) => {
                this.$store.commit('job/REMOVE_REACTION', ({
                    commentId: e.comment.id,
                    reactionId: e.reactionId
                }))
            })
    },

    mounted () {
        this.updateFooterOffsetHeight()
    },

    methods: {
        fetchComments ($state) {
            this.$store.dispatch('job/fetchComments').then(() => {
                $state.loaded()
            })
        },

        isCurrentUser (authorId) {
            return app.currentUser.id === authorId
        },

        updateAttachments (assetIds) {
            this.form.attachments = assetIds
        },

        reply (comment) {
            this.$store.commit('job/ADD_PARENT_COMMENT', comment)
            this.$refs.commentsBox.$refs.editor.focus()
        },

        scrollToBottom () {
            this.$nextTick(() => {
                this.$refs.feed.scrollTop = this.$refs.feed.scrollHeight
            })
        },

        loaded () {
            this.assetsLoaded++

            this.$nextTick(() => {
                if (this.assetsLoaded === this.imageCount) {
                    this.scrollToBottom()
                }
            })
        },

        updateFooterOffsetHeight () {
            this.footerOffsetHeight = this.$refs.footer.offsetHeight
        }
    },

    computed: {
        ...mapState('job', ['job', 'comments']),
        ...mapGetters('job', ['imageCount', 'presentedComments']),
        ...mapState('tenant', ['users']),

        typingText () {
            if (this.typing.length === 0) {
                return null
            }

            if (this.typing.length === 1) {
                return `${this.typing[0]} is typing...`
            }

            if (this.typing.length === 2) {
                return `${this.typing[0]} and ${this.typing[1]} are typing...`
            }

            return `Several people are typing...`
        },

        feedStyles () {
            let marginBottom = '0'

            if (this.isMobile) {
                marginBottom = `${this.footerOffsetHeight}px`
            }

            return {
                'margin-bottom': marginBottom
            }
        }
    },

    components: {
        Avatar,
        CommentBox,
        Comment,
        Activity,
        InfiniteLoading
    }
}
</script>
